import { TableCell } from 'types/common/table';
import { getUtcUsFormatDate } from 'modules/utils/time';
import EditDateButton from 'components/backorder/table/currentPlanTable/EditDateButton';
import * as React from 'react';
import { toMantissaWithThousandComma } from 'modules/utils/number';
import { InfoIcon } from '@closet-design-system/core';
import Tooltip from '@material-ui/core/Tooltip';
import { BackOrderHistory, PlanInformation } from 'types/api/backorder';
import DeleteBackOrderButton from 'components/backorder/table/backorderHistory/DeleteBackOrderButton';

//* plan information 내 current plan table
export const currentPlanTableColumns: TableCell<PlanInformation>[] = [
  {
    name: 'Plan Type',
    key: 'planType',
    align: 'center',
  },
  {
    name: 'Payment Method',
    key: 'paymentMethod',
    align: 'center',
    render: ({ paymentMethod }) => paymentMethod ?? '-',
  },
  {
    name: 'Order Date',
    key: 'orderDate',
    align: 'center',
    render: ({ orderDate }) => (orderDate ? getUtcUsFormatDate(orderDate) : '-'),
  },
  {
    name: (
      <>
        Subscription Term /<br />
        Start Date
      </>
    ),
    key: 'startDate',
    align: 'center',
    render: ({ subscriptionPeriod: { startDate } }) => {
      return startDate ? getUtcUsFormatDate(startDate) : '-';
    },
  },
  {
    name: (
      <>
        Subscription Term /<br />
        End Date
      </>
    ),
    key: 'endDate',
    align: 'center',
    render: data => {
      const {
        subscriptionId,
        canEndDateEdit,
        subscriptionPeriod: { endDate },
      } = data;
      return (
        /**
         * current plan endDate 수정 조건
         * 현재의 end date 이전 날짜도 선택 가능
         * 과거 날짜는 선택 불가 - 오늘 날짜는 가능하지만, 어제 날짜는 안됨 (UTC 기준)
         * */
        <EditDateButton
          subscriptionId={subscriptionId}
          type="end"
          targetDate={endDate}
          canEdit={canEndDateEdit}
        />
      );
    },
  },
  {
    name: 'Current Credit Balance',
    key: 'currentCreditBalance',
    align: 'center',
    render: ({ currentCreditBalance }) => toMantissaWithThousandComma(currentCreditBalance),
  },
  {
    name: 'CLO-SET Manager',
    key: 'backorderByEmail',
    align: 'center',
  },
];

export const currentPlanTableHeader: TableCell[] = currentPlanTableColumns.map(column => {
  return {
    name: column.name,
    key: column.key,
  };
});

//* back order history list table
export const backOrderHistoryTableColumns: TableCell<BackOrderHistory>[] = [
  {
    name: 'Plan Type',
    key: 'planType',
    align: 'left',
  },
  {
    name: 'Payment Method',
    key: 'paymentMethod',
    align: 'left',
    render: ({ paymentMethod }) => paymentMethod ?? '-',
  },
  {
    name: 'Order Date',
    key: 'orderDate',
    align: 'left',
    render: ({ orderDate }) => (orderDate ? getUtcUsFormatDate(orderDate) : '-'),
  },
  {
    name: (
      <>
        Subscription Term /<br />
        Start Date
      </>
    ),
    key: 'startDate',
    align: 'center',
    render: data => {
      const {
        subscriptionId,
        canStartDateEdit,
        subscriptionPeriod: { startDate },
      } = data;
      if (!canStartDateEdit) {
        return startDate ? getUtcUsFormatDate(startDate) : '-';
      }
      return (
        <EditDateButton
          subscriptionId={subscriptionId}
          type="start"
          targetDate={startDate}
          canEdit
        />
      );
    },
  },
  {
    name: (
      <>
        Subscription Term /<br />
        End Date
      </>
    ),
    key: 'endDate',
    align: 'center',
    render: data => {
      const {
        subscriptionId,
        canEndDateEdit,
        subscriptionPeriod: { endDate },
      } = data;
      if (!canEndDateEdit) {
        return endDate ? getUtcUsFormatDate(endDate) : '-';
      }
      return (
        <EditDateButton subscriptionId={subscriptionId} type="end" targetDate={endDate} canEdit />
      );
    },
  },
  {
    name: (
      <>
        Credit Balance of
        <br />
        the End Date
        <Tooltip
          title={
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontWeight: 'bolder' }}>Notice</div>
              <div>
                For an accurate True-up Fee calculation, please check Tableau to see if the group
                has overused Rendering Usage, Embed View, and API Call.
              </div>
            </div>
          }
          arrow
          placement="top"
        >
          <span>
            <InfoIcon size="12px" />
          </span>
        </Tooltip>
      </>
    ),
    key: 'creditBalance',
    align: 'center',
    render: ({ creditBalance }) =>
      creditBalance ? toMantissaWithThousandComma(creditBalance) : '-',
  },
  {
    name: 'CLO-SET Manager',
    key: 'backorderByEmail',
    align: 'center',
  },
  {
    name: 'Status',
    key: 'status',
    align: 'left',
    render: ({ subscriptionId, status, reservedCredit }) => {
      if (status === 'Reserved') {
        return (
          <DeleteBackOrderButton
            subscriptionId={subscriptionId}
            status={status}
            reservedCredit={reservedCredit}
          />
        );
      }
      return status;
    },
  },
];

export const backOrderHistoryTableHeader: TableCell[] = backOrderHistoryTableColumns.map(column => {
  return {
    name: column.name,
    key: column.key,
  };
});
